import { useState } from 'react';
import { formatDuration, type ScoredCandidate } from '@srcbattle/shared';
import { Button } from './ui.tsx';

/**
 * The confirm step for a Spotify link.
 *
 * The submitter always confirms, even when one candidate is obviously right.
 * Auto-accepting a confident match means occasionally slipping a cover or a
 * live version into the bracket with nobody noticing until it plays.
 */
export function CandidatePicker({
  track,
  candidates,
  busy,
  onConfirm,
  onCancel,
}: {
  track: { title: string; artist: string; durationMs: number; albumArtUrl: string | null };
  candidates: ScoredCandidate[];
  busy: boolean;
  onConfirm: (videoId: string) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<string>(
    candidates.find((c) => c.recommended)?.videoId ?? candidates[0]?.videoId ?? '',
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-raised p-3">
        {track.albumArtUrl && (
          <img src={track.albumArtUrl} alt="" className="size-12 rounded" />
        )}
        <div className="min-w-0">
          <p className="truncate font-medium">{track.title}</p>
          <p className="truncate text-sm text-text-muted">
            {track.artist} · {formatDuration(track.durationMs)}
          </p>
        </div>
      </div>

      <p className="text-sm text-text-muted">
        Pick the matching video. The right one is usually the same length as the track.
      </p>

      <ul className="flex flex-col gap-2">
        {candidates.map((candidate) => {
          const isSelected = candidate.videoId === selected;
          return (
            <li key={candidate.videoId}>
              <button
                type="button"
                onClick={() => setSelected(candidate.videoId)}
                aria-pressed={isSelected}
                className={`flex w-full items-start gap-3 rounded-lg border p-2.5 text-left transition-colors ${
                  isSelected
                    ? 'border-accent bg-accent/10'
                    : 'border-border hover:border-border-strong'
                }`}
              >
                {candidate.thumbnailUrl ? (
                  <img
                    src={candidate.thumbnailUrl}
                    alt=""
                    className="h-12 w-20 shrink-0 rounded object-cover"
                  />
                ) : (
                  <span className="h-12 w-20 shrink-0 rounded bg-surface-raised" />
                )}

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{candidate.title}</span>
                    {candidate.recommended && (
                      <span className="shrink-0 rounded bg-success/20 px-1.5 py-0.5 text-xs text-success">
                        best match
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-text-faint">
                    {candidate.channelTitle} · {formatDuration(candidate.durationMs)}
                  </span>
                  {candidate.signals.length > 0 && (
                    <span className="mt-1 block text-xs text-text-muted">
                      {candidate.signals.join(' · ')}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy} className="flex-1">
          Cancel
        </Button>
        <Button onClick={() => onConfirm(selected)} loading={busy} disabled={!selected} className="flex-1">
          That's the one
        </Button>
      </div>

      <p className="text-xs text-text-faint">
        None of these? Cancel and paste a YouTube link instead.
      </p>
    </div>
  );
}
